if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
        this.error = false;
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const formData = new FormData(this.form);
        const feeVariantId = parseInt(String(this.dataset.cutWizardFeeVariantId || ''), 10);
        const feeUnitCents = parseInt(String(this.dataset.cutWizardFeeUnitCents || '1'), 10) || 1;
        const surchargeCentsPerPack = window.CutWizardCart?.readSurchargeCentsPerPack(formData) ?? 0;
        const useSurchargeLine =
          surchargeCentsPerPack > 0 && Number.isFinite(feeVariantId) && feeVariantId > 0 && window.CutWizardCart;

        if (useSurchargeLine) {
          this.addToCartWithSurchargeLine(formData, feeVariantId, feeUnitCents, surchargeCentsPerPack);
          return;
        }

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => this.handleAddResponse(response, formData.get('id')))
          .catch((e) => console.error(e))
          .finally(() => this.finishSubmit());
      }

      /** @param {FormData} formData @param {number} feeVariantId @param {number} feeUnitCents @param {number} surchargeCentsPerPack */
      addToCartWithSurchargeLine(formData, feeVariantId, feeUnitCents, surchargeCentsPerPack) {
        const variantId = formData.get('id');
        const quantity = parseInt(String(formData.get('quantity') || '1'), 10) || 1;
        const lineKey = window.CutWizardCart.createLineKey();
        const mainProperties = window.CutWizardCart.readPropertiesFromForm(formData);
        mainProperties._cut_wizard_line_key = lineKey;

        const surchargeQty = window.CutWizardCart.surchargeLineQuantity(
          surchargeCentsPerPack,
          quantity,
          feeUnitCents
        );

        /** @type {Array<{id: string|number, quantity: number, properties?: Record<string, string>}>} */
        const items = [
          {
            id: variantId,
            quantity,
            properties: mainProperties,
          },
          {
            id: feeVariantId,
            quantity: surchargeQty,
            properties: window.CutWizardCart.buildSurchargeProperties(lineKey, variantId, surchargeCentsPerPack),
          },
        ];

        /** @type {Record<string, unknown>} */
        const payload = { items };
        if (this.cart) {
          payload.sections = this.cart.getSectionsToRender().map((section) => section.id).join(',');
          payload.sections_url = window.location.pathname;
          this.cart.setActiveElement(document.activeElement);
        }

        fetch(`${routes.cart_add_url}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify(payload),
        })
          .then((response) => response.json())
          .then((response) => this.handleAddResponse(response, variantId))
          .catch((e) => console.error(e))
          .finally(() => this.finishSubmit());
      }

      /** @param {Record<string, unknown>} response @param {FormDataEntryValue|null} variantId */
      handleAddResponse(response, variantId) {
        if (response.status) {
          publish(PUB_SUB_EVENTS.cartError, {
            source: 'product-form',
            productVariantId: variantId,
            errors: response.errors || response.description,
            message: response.message,
          });
          this.handleErrorMessage(response.description);

          const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
          if (!soldOutMessage) return;
          this.submitButton.setAttribute('aria-disabled', true);
          this.submitButtonText.classList.add('hidden');
          soldOutMessage.classList.remove('hidden');
          this.error = true;
          return;
        }

        if (!this.cart) {
          window.location = window.routes.cart_url;
          return;
        }

        const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
        if (!this.error) {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'product-form',
            productVariantId: variantId,
            cartData: response,
          }).then(() => {
            CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
          });
        }
        this.error = false;
        const quickAddModal = this.closest('quick-add-modal');
        if (quickAddModal) {
          document.body.addEventListener(
            'modalClosed',
            () => {
              setTimeout(() => {
                CartPerformance.measure('add:paint-updated-sections', () => {
                  this.cart.renderContents(response);
                });
              });
            },
            { once: true }
          );
          quickAddModal.hide(true);
        } else {
          CartPerformance.measure('add:paint-updated-sections', () => {
            this.cart.renderContents(response);
          });
        }
      }

      finishSubmit() {
        this.submitButton.classList.remove('loading');
        if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
        if (!this.error) this.submitButton.removeAttribute('aria-disabled');
        this.querySelector('.loading__spinner').classList.add('hidden');
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
